import { useCallback, useEffect, useMemo, useState } from 'react';
import { getConversations, getMessages, postAnswer } from '../../services/clientChatApi';

const buildStorageKey = (teamId, clientId, userId) => {
  if (!teamId || !userId) return null;
  return `clientChat:${teamId}:${clientId || 'global'}:${userId}`;
};

const useClientChat = ({ isOpen, teamId, userId, clientId, projectId, clientName, activeView }) => {
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [createNewConversation, setCreateNewConversation] = useState(false);

  const storageKey = useMemo(
    () => buildStorageKey(teamId, clientId, userId),
    [teamId, clientId, userId]
  );

  useEffect(() => {
    if (!storageKey) return;
    const savedConversationId = localStorage.getItem(storageKey);
    if (savedConversationId) {
      setConversationId(savedConversationId);
    }
  }, [storageKey]);

  const loadConversation = useCallback(async () => {
    if (!teamId || !clientId || conversationId) return;
    setIsLoadingHistory(true);

    try {
      const convoRes = await getConversations({ teamId, clientId });
      const conversations = convoRes.data?.conversations || [];

      if (conversations.length === 0) {
        setConversationId(null);
        setMessages([]);
        if (storageKey) {
          localStorage.removeItem(storageKey);
        }
        return;
      }

      const latest = conversations[0];
      const convoId = latest.id;
      const msgRes = await getMessages({
        conversationId: convoId,
        teamId,
        clientId
      });

      const mappedMessages = (msgRes.data?.messages || [])
        .filter(m => m.role !== 'system')
        .map(m => ({
          id: m.id,
          type: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.content,
          timestamp: m.created_at ? new Date(m.created_at) : new Date()
        }));

      setConversationId(convoId);
      setMessages(mappedMessages);
      if (storageKey) {
        localStorage.setItem(storageKey, convoId);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      setConversationId(null);
      setMessages([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [teamId, clientId, storageKey]);

  useEffect(() => {
    if (!isOpen) return;
    loadConversation();
  }, [isOpen, loadConversation]);

  const sendMessage = useCallback(async () => {
    if (!teamId || !userId) {
      console.warn('[AI CHAT] send blocked', {
        teamIdPresent: !!teamId,
        userIdPresent: !!userId,
        clientIdPresent: !!clientId,
        projectIdPresent: !!projectId,
        view: activeView
      });
      return;
    }

    if (!chatInput.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: chatInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    setIsSending(true);

    try {
      const effectiveProjectId = projectId && projectId !== 'general' ? projectId : undefined;
      const payload = {
        team_id: teamId,
        user_id: userId,
        active_view: activeView,
        query: currentInput,
        ...(clientId ? { client_id: clientId } : {}),
        ...(effectiveProjectId ? { project_id: effectiveProjectId } : {}),
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(createNewConversation ? { create_new_conversation: true } : {})
      };

      console.debug('[AI CHAT] send', {
        teamId,
        userId,
        client_id: clientId,
        project_id: effectiveProjectId,
        view: activeView
      });

      console.debug('[AI CHAT] sending identifiers', {
        teamId,
        userId,
        clientId,
        projectId: effectiveProjectId || null,
        activeView,
        conversationId
      });

      const response = await postAnswer(payload);

      if (response.data?.conversation_id) {
        setConversationId(response.data.conversation_id);
        if (storageKey) {
          localStorage.setItem(storageKey, response.data.conversation_id);
        }
        setCreateNewConversation(false);
      }

      const aiMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response.data.answer,
        timestamp: new Date(),
        bullets: response.data.bullets || [],
        sources: response.data.sources || [],
        confidence: response.data.confidence || 'low',
        used_rag: response.data.used_rag || false
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI chat error:', error);

      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: error.response?.status === 503
          ? "I'm currently not available. Please try again later or contact support."
          : `I'm having trouble accessing information about ${clientName} right now. Please try again or contact support if the issue persists.`,
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  }, [teamId, clientId, userId, chatInput, conversationId, clientName, storageKey, projectId, activeView, createNewConversation]);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    sendMessage();
  }, [sendMessage]);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setCreateNewConversation(true);
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  return {
    messages,
    chatInput,
    setChatInput,
    isSending,
    isLoadingHistory,
    conversationId,
    startNewChat,
    sendMessage,
    loadConversation,
    handleSubmit
  };
};

export default useClientChat;
